// ---------------------------------------------------------------------------
// DocHelper — DOCX export from rendered Konva pages.
//
// Walks the rendered Konva page groups (produced by DocDslRenderer's
// DocPaginationEngine), reads _semantic tags, and produces a Word document
// via the `docx` npm package.
//
// Two strategies:
//   1. Semantic blocks (heading/paragraph/list/table/image) → proper DOCX elements
//   2. Visual blocks (chart/map/tile_row/bullet_list) → embedded PNG snapshots
//
// This ensures WYSIWYG: both the on-screen Konva preview and the DOCX output
// derive from the same rendered tree.
// ---------------------------------------------------------------------------

import Konva from "konva";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Pixel-to-DOCX unit conversion: DOCX uses EMU (English Metric Units).
const PX_TO_PT = 72 / 96; // 1 pixel ≈ 0.75 pt at 96 DPI
// Word page size & margins are expressed in twips (DXA, 1/20 pt). 1 inch = 1440 twips = 96 px.
const PX_TO_TWIP = 1440 / 96; // = 15

// Split a string with inline markdown (**bold**, *italic*) into styled run segments
// {text, bold, italic}, inheriting any base style. The DSL content carries these markers
// literally, so without this they export as visible "**…**". Only asterisks are treated as
// delimiters — underscores are left alone to avoid mangling identifiers like net_debt.
export function parseInlineMarkdownRuns(text, base = {}) {
  const bBold = !!base.bold, bItalic = !!base.italic;
  if (typeof text !== "string" || text === "") {
    return [{ text: text || "", bold: bBold, italic: bItalic }];
  }
  const re = /(\*\*)(.+?)\1|(\*)(.+?)\3/g;
  const out = [];
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push({ text: text.slice(last, m.index), bold: bBold, italic: bItalic });
    if (m[1]) {
      out.push({ text: m[2], bold: true, italic: bItalic });        // **bold** / __bold__
    } else {
      out.push({ text: m[4], bold: bBold, italic: true });          // *italic* / _italic_
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last), bold: bBold, italic: bItalic });
  return out.length ? out : [{ text, bold: bBold, italic: bItalic }];
}

// ---------------------------------------------------------------------------
// Collect semantic nodes from a Konva page group
// ---------------------------------------------------------------------------

/**
 * Walk a Konva Group depth-first, collecting nodes with _semantic tags.
 * Returns nodes in document order (top-to-bottom, left-to-right).
 */
export function collectSemanticBlocks(group, result = []) {
  const children = group.getChildren();

  // Sort children by y-position for document order
  const sorted = [...children].sort((a, b) => {
    const ay = a.y();
    const by = b.y();
    return ay - by;
  });

  for (const child of sorted) {
    const sem = child.getAttr("_semantic");
    if (sem && sem.blockType) {
      result.push(child);
      // For visual blocks (requiresImage), don't recurse — their internal
      // structure is captured as a PNG, not mapped to DOCX elements.
      if (sem.requiresImage) continue;
      // For table, recurse into children to collect cells
      if (sem.blockType === "table") {
        collectSemanticBlocks(child, result);
        continue;
      }
      // For list, recurse into children to collect items
      if (sem.blockType === "list") {
        collectSemanticBlocks(child, result);
        continue;
      }
      // For other semantic blocks (heading, paragraph, image), don't recurse
      continue;
    }
    // Non-semantic groups — recurse
    if (child instanceof Konva.Group) {
      collectSemanticBlocks(child, result);
    }
  }

  return result;
}

// ---------------------------------------------------------------------------
// Capture a Konva Group as a PNG data URL
// ---------------------------------------------------------------------------

/**
 * Render a Konva Group (or any node subtree) to a PNG Blob.
 * Creates a temporary Stage, draws the node, and captures via toDataURL.
 */
export async function captureGroupAsPNG(node, options = {}) {
  const { scale = 2 } = options; // 2x for good print quality

  // Calculate bounding box of the node
  const bbox = getNodeBoundingBox(node);
  if (!bbox) return null;

  const width = Math.round(bbox.width || 100);
  const height = Math.round(bbox.height || 100);

  // Create a temporary stage
  const stage = new Konva.Stage({
    width: width * scale,
    height: height * scale,
    container: document.createElement("div")
  });

  const layer = new Konva.Layer({ scale: { x: scale, y: scale } });
  stage.add(layer);

  // Clone the node or reposition it
  const nodeCopy = node.clone();
  nodeCopy.x(0);
  nodeCopy.y(0);
  layer.add(nodeCopy);

  // Draw synchronously
  layer.draw();

  // Capture
  const dataUrl = stage.toDataURL({ pixelRatio: 1 });
  stage.destroy();

  // Convert data URL to Blob
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return blob;
}

function getNodeBoundingBox(node) {
  if (node instanceof Konva.Group) {
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    for (const child of node.getChildren()) {
      const cx = child.x();
      const cy = child.y();
      const cw = child.width() || 0;
      const ch = child.height() || 0;
      if (cx + cw > maxX) maxX = cx + cw;
      if (cx < minX) minX = cx;
      if (cy + ch > maxY) maxY = cy + ch;
      if (cy < minY) minY = cy;
    }
    if (minX === Infinity) return { x: 0, y: 0, width: node.width() || 100, height: node.height() || 100 };
    return { x: minX, y: minY, width: maxX - minX || 100, height: maxY - minY || 100 };
  }
  return {
    x: node.x(), y: node.y(),
    width: node.width() || 100,
    height: node.height() || 100
  };
}

// ---------------------------------------------------------------------------
// DOCX element builders (used by `docx` npm package)
// ---------------------------------------------------------------------------

// These builders return the data needed to construct DOCX elements.
// The actual `docx` library calls are made when the user triggers export.
// We return plain objects that can be passed to `docx` constructors.

// Style conversion helpers — turn px-based values into Word units so the export
// visually mirrors the Konva preview.
export const pxToHalfPoint = (px) => Math.max(2, Math.round((px || 12) * PX_TO_PT * 2)); // docx run size = half-points
export const pxToTwip = (px) => Math.max(0, Math.round((px || 0) * PX_TO_TWIP));          // docx spacing/indent = twips
export const pxToBorderSize = (px) => Math.max(1, Math.round((px || 0.5) * PX_TO_PT * 8)); // docx border size = eighths of a point
// Normalise to a 6-digit hex (docx rejects 3-digit shorthand like "999").
const cleanHex = (c) => {
  if (typeof c !== "string" || !c) return undefined;
  let h = c.replace(/^#/, "").trim();
  if (h.length === 3) h = h.split("").map((ch) => ch + ch).join("");
  return /^[0-9a-fA-F]{6}$/.test(h) ? h : undefined;
};
// w:jc only accepts these; "justify" is not valid (it's "both").
const toAlignment = (a) => ({ left: "left", start: "left", center: "center", right: "right", end: "right", justify: "both", justified: "both", both: "both" }[a] || "left");

// Find the CustomText/Text node inside a rendered block group (heading/paragraph groups
// wrap their text node; list items & table cells already ARE the text node).
function findTextNode(node) {
  if (!node) return null;
  const cls = node.getClassName && node.getClassName();
  if (cls === "CustomText" || cls === "Text" || (typeof node.fontSize === "function" && Array.isArray(node.textArr))) {
    return node;
  }
  const kids = node.getChildren ? node.getChildren() : (node.children || []);
  for (const k of kids) {
    const found = findTextNode(k);
    if (found) return found;
  }
  return null;
}

// Read the actual rendered text style straight off the Konva node (mirrors PptHelper):
// font size, colour, family and line spacing. No hardcoded values — whatever the preview
// drew is what we export.
function readTextStyle(node) {
  if (!node) return {};
  const fs = typeof node.fontSize === "function" ? node.fontSize() : null;
  return {
    fontSizePx: fs || undefined,
    sizeHalfPt: fs ? pxToHalfPoint(fs) : undefined,
    color: cleanHex(typeof node.fill === "function" ? node.fill() : undefined),
    fontFamily: typeof node.fontFamily === "function" ? node.fontFamily() : undefined,
    lineHeight: typeof node.lineHeight === "function" ? node.lineHeight() : undefined,
    bold: typeof node.fontStyle === "function" && node.fontStyle() === "bold",
    // Konva text padding (top/bottom) contributes to the block's vertical breathing room.
    padding: typeof node.padding === "function" ? (node.padding() || 0) : 0
  };
}

export function buildDocxHeading(semantic, konvaNode, options = {}) {
  const level = semantic.headingLevel || 1;
  const text = extractTextFromNode(konvaNode);
  const style = readTextStyle(findTextNode(konvaNode));

  return {
    type: "heading",
    headingLevel: Math.min(level, 5),
    text,
    style: { ...style, color: style.color || cleanHex(semantic.color) },
    bold: semantic.bold !== false,
    rule: !!semantic.rule,
    spacingBefore: pxToTwip((semantic.spacingBeforePx || 0) + (style.padding || 0)),
    spacingAfter: pxToTwip((semantic.spacingAfterPx || 0) + (style.padding || 0)),
    id: semantic.headingId || null
  };
}

export function buildDocxParagraph(semantic, konvaNode, options = {}) {
  const runs = semantic.runs || [];
  const style = readTextStyle(findTextNode(konvaNode));

  const children = runs.flatMap(r =>
    parseInlineMarkdownRuns(r.text || "", { bold: r.bold, italic: r.italic })
      .map(seg => ({ text: seg.text, bold: seg.bold, italic: seg.italic }))
  );

  return {
    type: "paragraph",
    children,
    style: { ...style, color: style.color || cleanHex(semantic.color) },
    alignment: toAlignment(semantic.alignment),
    spacingBefore: pxToTwip(semantic.spacingBeforePx || 0),
    spacingAfter: pxToTwip((semantic.spacingAfterPx != null ? semantic.spacingAfterPx : 8) + (style.padding || 0))
  };
}

export function buildDocxListItem(semantic, konvaNode, options = {}) {
  const text = extractTextFromNode(konvaNode);
  const style = readTextStyle(konvaNode); // list item IS a CustomText node

  return {
    type: "listItem",
    text,
    runs: parseInlineMarkdownRuns(text),
    listType: semantic.listType || "bullet",
    level: semantic.listLevel || 0,
    index: semantic.listIndex || 0,
    style: { ...style, color: style.color || cleanHex(semantic.color) },
    indent: pxToTwip(semantic.indentPx || 20),
    spacingAfter: pxToTwip(semantic.spacingAfterPx != null ? semantic.spacingAfterPx : 4)
  };
}

export function buildDocxTable(semantic, konvaNode, options = {}) {
  // Walk children to collect cells by row/col
  const cells = [];
  const children = konvaNode.getChildren ? konvaNode.getChildren() : [];

  for (const child of children) {
    const sem = child.getAttr ? child.getAttr("_semantic") : null;
    if (sem && sem.blockType === "tableCell") {
      cells.push({
        row: sem.row,
        col: sem.col,
        isHeader: sem.isHeader,
        colspan: sem.colspan || 1,
        text: extractTextFromNode(child),
        style: readTextStyle(child),
        fill: cleanHex(sem.fill)
      });
    }
  }

  // Group by row
  const totalRows = semantic.totalRows || 0;
  const totalCols = semantic.totalCols || 0;
  const rows = [];
  for (let ri = 0; ri < totalRows; ri++) {
    const rowCells = cells.filter(c => c.row === ri);
    rows.push(rowCells);
  }

  return {
    type: "table",
    rows,
    totalCols,
    cellPadXPx: semantic.cellPadXPx != null ? semantic.cellPadXPx : 4,
    cellPadYPx: semantic.cellPadYPx != null ? semantic.cellPadYPx : 2,
    rowHeightPx: semantic.rowHeightPx || 0,
    borderColor: cleanHex(semantic.borderColor) || "cccccc",
    borderWidthPx: semantic.borderWidthPx || 0.5,
    headerLineColor: cleanHex(semantic.headerLineColor) || "999999",
    headerLineWidthPx: semantic.headerLineWidthPx || 1.5
  };
}

export function buildDocxImage(semantic, konvaNode, options = {}) {
  const text = extractTextFromNode(konvaNode);
  const src = semantic.src || "";

  // Find the image data — either from crystallized data or the Konva node
  // For now, return a placeholder — real implementation would use CustomImage's data
  return {
    type: "image",
    src,
    caption: text
  };
}

// ---------------------------------------------------------------------------
// Extract text from a Konva node (CustomText or any node with text)
// ---------------------------------------------------------------------------

export function extractTextFromNode(node) {
  if (!node) return "";

  // CustomText stores text via .text() or .attrs.text
  if (node.text) {
    const t = typeof node.text === "function" ? node.text() : node.text;
    return t || "";
  }

  // Check attrs
  if (node.attrs && node.attrs.text) return node.attrs.text || "";

  // Check for children with text
  if (node.getChildren) {
    for (const child of node.getChildren()) {
      const t = extractTextFromNode(child);
      if (t) return t;
    }
  }

  return "";
}

// ---------------------------------------------------------------------------
// Main export function
// ---------------------------------------------------------------------------

/**
 * Export an array of Konva page groups (from DocDslRenderer) to a DOCX Blob.
 *
 * @param {Konva.Group[]} pages - array of page groups (one per page)
 * @param {Object} dsl - the original DocDSL (for chrome/page config)
 * @param {Object} options - export options
 * @returns {Promise<Blob>} DOCX file as a Blob
 */
export async function exportPagesToDocx(pages, dsl, options = {}) {
  const pageConfig = dsl?.page || {};
  const chrome = dsl?.chrome || null;
  const pageWidth = pageConfig.width || 816;
  const pageHeight = pageConfig.height || 1056;
  const margins = pageConfig.margins || { top: 40, bottom: 40, left: 32, right: 32 };

  // Bisection helper: skip whole component categories to isolate what Word rejects.
  // Categories: "heading", "paragraph", "listItem", "table", "image", "visual", "header", "footer".
  const skip = new Set(Array.isArray(options.skip) ? options.skip : []);

  // Collect semantic nodes from all pages
  const allSections = [];

  for (let pi = 0; pi < pages.length; pi++) {
    const pageGroup = pages[pi];
    const pageNumber = pi + 1;

    // Collect semantic blocks on this page
    const semanticNodes = [];
    collectSemanticBlocks(pageGroup, semanticNodes);

    // Build section content
    const sectionChildren = [];

    for (const node of semanticNodes) {
      const sem = node.getAttr("_semantic");
      if (!sem) continue;

      switch (sem.blockType) {
        case "heading": {
          if (skip.has("heading")) break;
          sectionChildren.push(buildDocxHeading(sem, node));
          break;
        }
        case "paragraph": {
          if (skip.has("paragraph")) break;
          sectionChildren.push(buildDocxParagraph(sem, node));
          break;
        }
        case "listItem": {
          if (skip.has("listItem")) break;
          sectionChildren.push(buildDocxListItem(sem, node));
          break;
        }
        case "table": {
          if (skip.has("table")) break;
          sectionChildren.push(buildDocxTable(sem, node));
          break;
        }
        case "image": {
          if (skip.has("image")) break;
          sectionChildren.push(buildDocxImage(sem, node));
          break;
        }
        case "chart":
        case "map":
        case "tile_row":
        case "heatmap":
        case "progress":
        case "textBlock":
        case "bulletList":
        case "legend": {
          if (skip.has("visual")) break;
          // Visual blocks → embed as PNG
          try {
            const imageBlob = await captureGroupAsPNG(node);
            if (imageBlob) {
              // docx needs the raw bytes (a Blob is passed through unconverted) and an
              // explicit image type — without `type` the media part is named ".undefined",
              // which has no content type and triggers Word's "unreadable content" warning.
              const imageData = new Uint8Array(await imageBlob.arrayBuffer());
              sectionChildren.push({
                type: "image",
                imageData,
                imageType: "png",
                width: node.width() || 400,
                height: node.height() || 200
              });
            }
          } catch (e) {
            console.warn("Failed to capture visual block as PNG:", e);
          }
          break;
        }
      }
    }

    // Build page chrome (header/footer)
    let headerContent = null;
    let footerContent = null;
    if (chrome?.header && !skip.has("header")) {
      headerContent = {
        left: chrome.header.left?.replace("{{page}}", String(pageNumber)).replace("{{total}}", String(pages.length)) || "",
        right: chrome.header.right?.replace("{{page}}", String(pageNumber)).replace("{{total}}", String(pages.length)) || ""
      };
    }
    if (chrome?.footer && !skip.has("footer")) {
      footerContent = {
        left: chrome.footer.left?.replace("{{page}}", String(pageNumber)).replace("{{total}}", String(pages.length)).replace("{{date}}", new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })) || "",
        right: chrome.footer.right?.replace("{{page}}", String(pageNumber)).replace("{{total}}", String(pages.length)).replace("{{date}}", new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })) || ""
      };
    }

    allSections.push({
      children: sectionChildren,
      page: { width: pageWidth, height: pageHeight, margins, number: pageNumber },
      header: headerContent,
      footer: footerContent
    });
  }

  return {
    sections: allSections,
    totalPages: pages.length
  };
}

// ---------------------------------------------------------------------------
// Build the actual DOCX using the `docx` npm package
// (Called from the UI when user triggers export)
// ---------------------------------------------------------------------------

/**
 * Convert the collected section data into a real DOCX Blob.
 * Requires `docx` npm package: `npm install docx`
 */
export async function buildDocxFromSections(sectionData, options = {}) {
  // Dynamic import — the `docx` library is used only at export time
  const { Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell, WidthType,
          HeadingLevel, PageBreak, ImageRun, Header, Footer, Numbering, Tab, TabStopType } = await importDocx();

  // Build a styled run from a parsed segment, applying the node-derived style (size/colour/font).
  const mkRun = (seg, style = {}) => new TextRun({
    text: seg.text || "",
    bold: !!seg.bold,
    italics: !!seg.italic,
    ...(style.sizeHalfPt ? { size: style.sizeHalfPt } : {}),
    ...(style.color ? { color: style.color } : {}),
    ...(style.fontFamily ? { font: style.fontFamily } : {})
  });
  // Paragraph spacing from per-item values. Line spacing is the ABSOLUTE pitch
  // (lineHeight × fontSize, in twips) via lineRule "atLeast" — Word's "auto" rule bakes in
  // ~1.15× leading, so a multiple there over-spaces vs the Konva preview.
  const spacingFor = (b, style = {}) => {
    const out = {
      ...(b.spacingBefore ? { before: b.spacingBefore } : {}),
      ...(b.spacingAfter != null ? { after: b.spacingAfter } : {})
    };
    if (style.lineHeight && style.fontSizePx) {
      out.line = pxToTwip(style.lineHeight * style.fontSizePx);
      out.lineRule = "atLeast";
    }
    return out;
  };

  // Only emit numbering definitions when list items actually survived the export
  // (so "skip listItem" fully removes numbering.xml for clean bisection).
  const hasListItems = (sectionData.sections || []).some(
    s => (s.children || []).some(c => c?.type === "listItem")
  );

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: {
            font: options.font || "Calibri",
            size: 24 // 12pt in half-points
          }
        }
      }
    },
    ...(hasListItems ? { numbering: {
      config: [
        {
          reference: "bullets",
          levels: [
            { level: "0", format: "bullet", text: "•", alignment: "left" }
          ]
        },
        {
          reference: "numbered",
          levels: [
            { level: "0", format: "decimal", text: "%1.", alignment: "left" }
          ]
        }
      ]
    } } : {}),
    sections: sectionData.sections.map(section => {
      const children = [];
      let listIdCounter = 0;
      let currentListType = null;

      for (const child of section.children) {
        switch (child.type) {
          case "heading": {
            const headingLevelMap = {
              1: HeadingLevel.HEADING_1,
              2: HeadingLevel.HEADING_2,
              3: HeadingLevel.HEADING_3,
              4: HeadingLevel.HEADING_4,
              5: HeadingLevel.HEADING_5
            };
            const hl = headingLevelMap[child.headingLevel] || HeadingLevel.HEADING_1;
            children.push(new Paragraph({
              heading: hl,
              children: parseInlineMarkdownRuns(child.text, { bold: child.bold }).map(seg => mkRun(seg, child.style)),
              spacing: spacingFor(child, child.style),
              // mirror the preview's H1/H2 underline rule
              ...(child.rule ? { border: { bottom: { style: "single", size: 6, color: "333333", space: 1 } } } : {})
            }));
            break;
          }
          case "paragraph": {
            children.push(new Paragraph({
              children: child.children.map(seg => mkRun(seg, child.style)),
              alignment: child.alignment,
              spacing: spacingFor(child, child.style)
            }));
            break;
          }
          case "listItem": {
            // DOCX numbering: different reference per list type
            const ref = child.listType === "numbered" ? "numbered" : "bullets";
            const liRuns = (child.runs && child.runs.length ? child.runs : parseInlineMarkdownRuns(child.text || ""));
            children.push(new Paragraph({
              numbering: { reference: ref, level: child.level || 0 },
              children: liRuns.map(seg => mkRun(seg, child.style)),
              // match the preview's bullet indent
              ...(child.indent ? { indent: { left: child.indent, hanging: child.indent } } : {}),
              spacing: spacingFor(child, child.style)
            }));
            break;
          }
          case "table": {
            // Column count: prefer the semantic count, else the widest row. Must be ≥1 —
            // docx's TableCell builds a width element even from an undefined width and crashes.
            const cols = (child.totalCols && child.totalCols > 0)
              ? child.totalCols
              : Math.max(1, ...child.rows.map(r => r.length));
            // Use absolute (DXA/twips) widths sized to the page content box. Word is
            // unreliable with the percentage-string form ("33%") for w:tblW/w:tcW.
            const usableTwip = Math.max(
              cols,
              Math.round(((section.page?.width || 816)
                - (section.page?.margins?.left || 32)
                - (section.page?.margins?.right || 32)) * PX_TO_TWIP)
            );
            const cellTwip = Math.max(1, Math.floor(usableTwip / cols));
            const rowHeightTwip = pxToTwip(child.rowHeightPx);
            // Light grid borders matching the preview; header row carries a heavier underline.
            const gridBorder = { style: "single", size: pxToBorderSize(child.borderWidthPx), color: child.borderColor };
            const tableBorders = {
              top: gridBorder, bottom: gridBorder, left: gridBorder, right: gridBorder,
              insideHorizontal: gridBorder, insideVertical: gridBorder
            };
            const headerUnderline = { style: "single", size: pxToBorderSize(child.headerLineWidthPx), color: child.headerLineColor };
            // docx throws on a row with no cells — drop empties.
            const populatedRows = (child.rows || []).filter(r => r && r.length > 0);
            if (populatedRows.length === 0) break;
            const tableRows = populatedRows.map(rowCells => {
              return new TableRow({
                // mirror the preview's fixed row height (grows if content needs it)
                ...(rowHeightTwip ? { height: { value: rowHeightTwip, rule: "atLeast" } } : {}),
                children: rowCells.map(cell => {
                  const cs = cell.style || {};
                  return new TableCell({
                    children: [new Paragraph({
                      children: parseInlineMarkdownRuns(cell.text || "", { bold: cell.isHeader }).map(seg => mkRun(seg, cs)),
                      // kill Word's default in-cell paragraph spacing; keep the preview's tight line pitch
                      spacing: {
                        before: 0,
                        after: 0,
                        ...(cs.lineHeight && cs.fontSizePx ? { line: pxToTwip(cs.lineHeight * cs.fontSizePx), lineRule: "atLeast" } : {})
                      }
                    })],
                    width: { size: cellTwip, type: WidthType.DXA },
                    // cell fill from the preview (header fill, or any per-cell fill)
                    ...(cell.fill ? { shading: { type: "clear", color: "auto", fill: cell.fill } } : {}),
                    // heavier underline beneath the header row
                    ...(cell.isHeader ? { borders: { bottom: headerUnderline } } : {})
                  });
                })
              });
            });
            children.push(new Table({
              rows: tableRows,
              width: { size: cellTwip * cols, type: WidthType.DXA },
              borders: tableBorders,
              // explicit grid so <w:tblGrid> matches the cells (avoids bogus default gridCols)
              columnWidths: Array.from({ length: cols }, () => cellTwip),
              // cell padding to match the preview (text drawn at x+4, y+2)
              margins: {
                marginUnitType: WidthType.DXA,
                left: pxToTwip(child.cellPadXPx),
                right: pxToTwip(child.cellPadXPx),
                top: pxToTwip(child.cellPadYPx),
                bottom: pxToTwip(child.cellPadYPx)
              }
            }));
            break;
          }
          case "image": {
            if (child.imageData) {
              children.push(new Paragraph({
                children: [new ImageRun({
                  data: child.imageData,
                  type: child.imageType || "png", // required — drives the media part's extension + content type
                  // docx takes the display size in pixels under `transformation` (it converts to EMU itself)
                  transformation: { width: Math.round(child.width), height: Math.round(child.height) }
                })],
                alignment: "center"
              }));
            }
            break;
          }
        }
      }

      // Page properties — docx expects twips (DXA) for page size and margins,
      // and the size goes under `page.size.{width,height}` (a bare page.width is ignored).
      const pageWidthTwip = Math.round(section.page.width * PX_TO_TWIP);
      const pageHeightTwip = Math.round(section.page.height * PX_TO_TWIP);
      const marginTop = Math.round((section.page.margins?.top || 40) * PX_TO_TWIP);
      const marginBottom = Math.round((section.page.margins?.bottom || 40) * PX_TO_TWIP);
      const marginLeft = Math.round((section.page.margins?.left || 32) * PX_TO_TWIP);
      const marginRight = Math.round((section.page.margins?.right || 32) * PX_TO_TWIP);

      // Left text + right text on one line via a right-aligned tab stop at the content edge.
      // (Previously used alignment:"justify" — not a valid w:jc value, which made Word reject the file.)
      const contentTwip = Math.max(1, pageWidthTwip - marginLeft - marginRight);
      const chromeParagraph = (chrome, fontSize) => {
        const left = chrome?.left || "";
        const right = chrome?.right || "";
        const runs = [new TextRun({ text: left, size: fontSize, color: "888888" })];
        if (right) {
          runs.push(new TextRun({ children: [new Tab()], size: fontSize }));
          runs.push(new TextRun({ text: right, size: fontSize, color: "888888" }));
        }
        return new Paragraph({
          children: runs,
          ...(right ? { tabStops: [{ type: TabStopType.RIGHT, position: contentTwip }] } : {})
        });
      };

      return {
        properties: {
          page: {
            size: { width: pageWidthTwip, height: pageHeightTwip },
            margin: { top: marginTop, bottom: marginBottom, left: marginLeft, right: marginRight }
          }
        },
        headers: section.header
          ? {
              default: new Header({
                children: [chromeParagraph(section.header, 16)]
              })
            }
          : undefined,
        footers: section.footer
          ? {
              default: new Footer({
                children: [chromeParagraph(section.footer, 14)]
              })
            }
          : undefined,
        children
      };
    })
  });

  const blob = await Packer.toBlob(doc);
  return blob;
}

// ---------------------------------------------------------------------------
// Runtime loader — loads `docx` at export time
//
// We deliberately do NOT `import "docx"`: its published bundle ships modern
// class syntax that Create React App's (unejected) babel-loader can't compile
// for node_modules. Instead we load the prebuilt UMD/IIFE bundle as a plain
// <script> at export time, keeping docx out of the webpack/babel build
// entirely. The bundle is vendored to ui/public/docx.iife.js (kept fresh by
// the `vendor:docx` npm script) and exposes a global `window.docx`.
// ---------------------------------------------------------------------------

const DOCX_SCRIPT_SRC = "/docx.iife.js";
let _docxPromise = null;

function importDocx() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("DOCX export is only available in the browser."));
  }
  if (window.docx) {
    return Promise.resolve(window.docx);
  }
  if (_docxPromise) {
    return _docxPromise;
  }
  _docxPromise = new Promise((resolve, reject) => {
    const fail = (msg) => {
      _docxPromise = null;
      reject(new Error(msg));
    };
    const finish = () => {
      if (window.docx) {
        resolve(window.docx);
      } else {
        fail(`Loaded ${DOCX_SCRIPT_SRC} but global \`docx\` is not defined.`);
      }
    };
    // Reuse an in-flight/previous load if the tag already exists
    const existing = document.querySelector("script[data-docx-loader]");
    if (existing) {
      existing.addEventListener("load", finish, { once: true });
      existing.addEventListener("error", () => fail(`Failed to load ${DOCX_SCRIPT_SRC}`), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = DOCX_SCRIPT_SRC;
    script.async = true;
    script.setAttribute("data-docx-loader", "true");
    script.onload = finish;
    script.onerror = () => {
      script.remove();
      fail(
        `The docx bundle (${DOCX_SCRIPT_SRC}) failed to load. Vendor it with: ` +
        `cd ui && npm run vendor:docx (copies node_modules/docx/dist/index.iife.js into public/).`
      );
    };
    document.head.appendChild(script);
  });
  return _docxPromise;
}

// ---------------------------------------------------------------------------
// High-level export API (matching PptHelper pattern)
// ---------------------------------------------------------------------------

/**
 * Create and download a DOCX document from rendered Konva pages.
 *
 * @param {Konva.Group[]} pages - page groups from DocDslRenderer
 * @param {Object} dsl - the original DocDSL
 * @param {Object} options - { filename, font }
 */
export async function exportDocDslToDocx(pages, dsl, options = {}) {
  const filename = options.filename || (dsl.title ? dsl.title.replace(/\s+/g, "_") : "document") + ".docx";

  // Collect section data
  const sectionData = await exportPagesToDocx(pages, dsl, options);

  // Build DOCX Blob
  const blob = await buildDocxFromSections(sectionData, options);

  // Trigger download
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return blob;
}

export default { exportPagesToDocx, buildDocxFromSections, exportDocDslToDocx, collectSemanticBlocks, captureGroupAsPNG };
